import { BimActKc2OtherStatusEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';

import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { Control, UseFormWatch } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';
import { useNavigate, useParams } from 'react-router-dom';

import type { IBimActKc2OtherStatusesListItem } from '../../types';

const toOnCheckMessage = (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '0 24px' }}>
        <div>
            После перевода в статус «На проверке» внесение изменений в рабочей области Акта в столбец «Стоимость (План)»
            будет недоступно.
        </div>
        <div style={{ marginTop: 20 }}>Вы уверены, что хотите изменить статус на «На проверке»?</div>
    </div>
);

const noContractorAddress =
    'Перевод в статус «На проверке» невозможен. Необходимо заполнить параметр «Адрес Подрядчика» в карточке Договора.';

const toAcceptedMessage =
    'Вы уверены, что хотите изменить статус на «Согласован»? Дальнейшая работа по Акту КС-2 будет недоступна.';

const toOnCheckMessageFromAccepted = (
    <div style={{ padding: '20px' }}>Вы уверены, что хотите изменить статус на «На проверке»?</div>
);

const cancelButtonText = 'Не изменять';
const confirmButtonText = 'Изменить';

const confirmButtonTextToContract = 'Перейти в Договор';

interface IConfirmationsData {
    textConfirmationStatusChangeDialog: React.ReactNode | string;
    cancelButtonTextStatusChangeDialog: string;
    confirmButtonTextStatusChangeDialog: string;
}

interface IUseBimActKc2StatusChange {
    control: Control;
    watch: UseFormWatch<any>;
    setValue: UseFormSetValue<any>;
    initialStatusValue?: IBimActKc2OtherStatusesListItem;
}

export const useBimActKc2StatusChange = (props: IUseBimActKc2StatusChange) => {
    const { control, watch, setValue, initialStatusValue } = props;

    const navigate = useNavigate();
    const { id: projectId } = useParams();

    const contractorAddress = useWatch({ control, name: 'contractorAddress' });

    const [confirmationData, setConfirmationData] = useState<IConfirmationsData>();
    const [storedStatusValue, setStoredStatusValue] = useState<IBimActKc2OtherStatusesListItem>();
    const [statusValueToAccept, setStatusValueToAccept] = useState<IBimActKc2OtherStatusesListItem>();

    const actionFn = useCallback(() => {
        setValue('status', statusValueToAccept);

        setStoredStatusValue(statusValueToAccept);

        setStatusValueToAccept(undefined);
        setConfirmationData(undefined);

        if (confirmationData?.confirmButtonTextStatusChangeDialog === confirmButtonTextToContract) {
            navigate(`/projects/bim/${projectId}/contract/edit`);
        }
    }, [confirmationData?.confirmButtonTextStatusChangeDialog, navigate, projectId, setValue, statusValueToAccept]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn,
        });

    const onCloseStatusChangeDialog = useCallback(
        (isInSuccess) => {
            if (!isInSuccess) {
                setValue('status', storedStatusValue);

                setStatusValueToAccept(undefined);
                setConfirmationData(undefined);
            }
            handleCloseConfirmationDialog();
        },
        [handleCloseConfirmationDialog, setValue, storedStatusValue],
    );

    useEffect(() => {
        if (initialStatusValue) {
            setStoredStatusValue(initialStatusValue);
        }
    }, [initialStatusValue]);

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (name === 'status' && type === 'change') {
                const status = value?.status;

                if (
                    initialStatusValue?.id === BimActKc2OtherStatusEnum.Accepted &&
                    status?.id === BimActKc2OtherStatusEnum.OnCheck
                ) {
                    setConfirmationData({
                        textConfirmationStatusChangeDialog: toOnCheckMessageFromAccepted,
                        cancelButtonTextStatusChangeDialog: cancelButtonText,
                        confirmButtonTextStatusChangeDialog: confirmButtonText,
                    });
                    setStatusValueToAccept(status);
                    handleOpenConfirmationDialog();
                }

                if (
                    [BimActKc2OtherStatusEnum.OnCheck, BimActKc2OtherStatusEnum.Accepted].includes(status?.id) &&
                    initialStatusValue?.id !== BimActKc2OtherStatusEnum.Accepted
                ) {
                    if (status?.id === BimActKc2OtherStatusEnum.OnCheck) {
                        if (!contractorAddress) {
                            setConfirmationData({
                                textConfirmationStatusChangeDialog: noContractorAddress,
                                cancelButtonTextStatusChangeDialog: 'Отменить',
                                confirmButtonTextStatusChangeDialog: confirmButtonTextToContract,
                            });
                        }

                        setConfirmationData({
                            textConfirmationStatusChangeDialog: toOnCheckMessage,
                            cancelButtonTextStatusChangeDialog: cancelButtonText,
                            confirmButtonTextStatusChangeDialog: confirmButtonText,
                        });
                    }

                    if (status?.id === BimActKc2OtherStatusEnum.Accepted) {
                        setConfirmationData({
                            textConfirmationStatusChangeDialog: toAcceptedMessage,
                            cancelButtonTextStatusChangeDialog: cancelButtonText,
                            confirmButtonTextStatusChangeDialog: confirmButtonText,
                        });
                    }

                    setStatusValueToAccept(status);
                    handleOpenConfirmationDialog();
                }
            }
        }, []);

        return () => subscription.unsubscribe();
    }, [contractorAddress, handleOpenConfirmationDialog, watch, initialStatusValue?.id]);

    return {
        isOpenedStatusChangeDialog: isOpenConfirmationDialog,
        onCloseStatusChangeDialog,
        onActionChangeDialog: handleActionQuery,
        statusChangeConfirmationData: confirmationData,
    };
};
